import { useQuery } from '@tanstack/react-query';
import { get } from '@/shared/api/api-client';
import type { AppointmentProfileEnvelope } from '@/types/settings-api';

/**
 * Canonical query-key factory for the practitioner appointment-profile
 * list. Exported so PR-F mutations and stories/tests can target the
 * same cache slot without inlining the array literal.
 */
export function appointmentProfileListQueryKey(
  doctorSetupId: string | null | undefined,
  organizationId: string | null | undefined,
): readonly [string, string, string | null | undefined, string | null | undefined] {
  return [
    'settings',
    'appointment-profile-list',
    doctorSetupId,
    organizationId,
  ];
}

/**
 * Practitioner appointment-profile list — PROD-762 PR-E (Schedule sub-tab).
 *
 * GET /api/Doctor/AppointmentProfileList?practionerId={doctorSetupId}&organizationId={orgId}
 *
 * **Wire-typo on query string (verbatim)**: backend expects
 * `practionerId`, NOT `practitionerId`. Don't auto-correct — same
 * load-bearing magic-string pattern flagged across the settings
 * module. Verified against live cURL 2026-05-15.
 *
 * Returns `AppointmentProfileEnvelope`:
 *   {
 *     createAppointmentProfileRequest: AppointmentProfileEntry[],
 *     enabledLocation: ApptLocation[],
 *     isDeleted: string[]   // empty [] on GET
 *   }
 *
 * Each entry carries `id`, `profileName`, `minutesDuration`, `color`
 * (Flutter `Color(int)` hex like `"0xFFA7CAFF"`), `location`,
 * `speciality`, `appointmentType`, optional `reasonCode` + `billingProfile`.
 *
 * Flutter source: `settings_data_source_impl.dart:204-221`
 * (`getAppointmentProfile`). Flutter has an optional `&practiceId={id}`
 * filter on the query string for cross-practice scoping; we omit it
 * here since the Schedule tab is scoped to a single practitioner +
 * the org context — practice filtering happens server-side via the
 * practitioner record.
 *
 * Mutation (PR-F) ships on top of this hook once the POST cURL is
 * captured. Endpoint per Flutter: POST /api/Doctor/AppointmentProfile.
 */
export function useAppointmentProfileList(
  doctorSetupId: string | null | undefined,
  organizationId: string | null | undefined,
) {
  return useQuery({
    queryKey: appointmentProfileListQueryKey(doctorSetupId, organizationId),
    queryFn: ({ signal }) => {
      if (!doctorSetupId || !organizationId) {
        throw new Error(
          'useAppointmentProfileList called without doctorSetupId or organizationId',
        );
      }
      const qs = new URLSearchParams({
        practionerId: doctorSetupId, // verbatim typo per wire contract
        organizationId,
      });
      return get<AppointmentProfileEnvelope>(
        `/api/Doctor/AppointmentProfileList?${qs.toString()}`,
        { signal },
      );
    },
    enabled: !!doctorSetupId && !!organizationId,
  });
}
